import {Globe} from 'lucide-react'
import {useDroppable} from '@dnd-kit/core'
import {useAppConfig} from '@/hooks/use-app-config'
import type {AppConfig, LocalizedTypeValue} from '@/hooks/use-app-config'
import {useI18n} from '@/i18n'
import {cn} from '@/lib/utils'
import {AttributeEntityType} from '@/types/attribute-entity-type'
import {Badge} from '@/components/ui/badge'

interface TypeScopeFilterProps {
    entityType: AttributeEntityType
    value: string | null // null = all, '' = global, otherwise type code
    onChange: (value: string | null) => void
    className?: string
    /** When true, buttons register as drop targets and show a glow on drag-over */
    droppable?: boolean
    /** Optional count provider — receives the same value the button toggles on. */
    getCount?: (value: string | null) => number
}

const ENTITY_TYPE_KEY: Record<string, keyof AppConfig> = {
    [AttributeEntityType.IDENTITY]: 'identityTypes',
    [AttributeEntityType.PARTY]: 'partyTypes',
    [AttributeEntityType.RESOURCE]: 'resourceTypes',
    [AttributeEntityType.FUNCTION_GROUP]: 'functionGroupTypes',
    [AttributeEntityType.TARGET_SYSTEM]: 'systemTypes',
    [AttributeEntityType.CONNECTOR]: 'connectorTypes',
}

function getTypes(entityType: AttributeEntityType, config: ReturnType<typeof useAppConfig>): LocalizedTypeValue[] {
    const key = ENTITY_TYPE_KEY[entityType]
    if (!key) throw new Error(`TypeScopeFilter: no config key mapping for entityType "${entityType}"`)
    const value = config[key]
    return Array.isArray(value) ? value as LocalizedTypeValue[] : []
}

// ---- Droppable button sub-components ----

 
const DROPPABLE_ALL = 'type-filter-all'
const DROPPABLE_GLOBAL = 'type-filter-global'
export const TYPE_FILTER_DROP_ID_ALL = DROPPABLE_ALL
export const TYPE_FILTER_DROP_ID_GLOBAL = DROPPABLE_GLOBAL
export const TYPE_FILTER_DROP_ID_PREFIX = 'type-filter-type-'
export function typeFilterDropId(code: string): string {
    return `${TYPE_FILTER_DROP_ID_PREFIX}${code}`
}
 

interface DroppableButtonProps {
    droppableId: string
    active: boolean
    droppable: boolean
    className?: string
    onClick: () => void
    'data-testid'?: string
    children: React.ReactNode
}

function DroppableButton({droppableId, active, droppable, className, onClick, 'data-testid': testId, children}: DroppableButtonProps) {
    const {isOver, setNodeRef} = useDroppable({
        id: droppableId,
        disabled: !droppable,
    })

    return (
        <button
            ref={droppable ? setNodeRef : undefined}
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={cn(
                'flex shrink-0 items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                active
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/80',
                droppable && isOver && 'ring-2 ring-primary ring-offset-1 bg-primary/10 text-foreground',
                className,
            )}
        >
            {children}
        </button>
    )
}

function CountBadge({count}: {count: number}) {
    if (count <= 0) return null
    return (
        <Badge
            variant="secondary"
            className="ml-1.5 text-xs font-normal bg-muted-foreground/20 text-foreground hover:bg-muted-foreground/20 hover:text-foreground tabular-nums"
        >
            {count}
        </Badge>
    )
}

export function TypeScopeFilter({entityType, value, onChange, className, droppable = false, getCount}: TypeScopeFilterProps) {
    const i18n = useI18n()
    const config = useAppConfig()
    const types = getTypes(entityType, config)

    if (types.length === 0) return null

    const isAll = value === null
    const isGlobal = value === ''

    return (
        <div className={cn('flex flex-wrap rounded-lg border bg-muted p-0.5 gap-0.5 max-w-full', className)}>
            <DroppableButton
                droppableId={DROPPABLE_ALL}
                active={isAll}
                droppable={droppable}
                onClick={() => onChange(null)}
                data-testid="type-scope-all"
            >
                {i18n.typeScopeFilter.all}
                {getCount && <CountBadge count={getCount(null)}/>}
            </DroppableButton>
            <DroppableButton
                droppableId={DROPPABLE_GLOBAL}
                active={isGlobal}
                droppable={droppable}
                onClick={() => onChange('')}
                data-testid="type-scope-global"
            >
                <Globe className="h-3.5 w-3.5" />
                {i18n.typeScopeFilter.global}
                {getCount && <CountBadge count={getCount('')}/>}
            </DroppableButton>
            {types.map(type => (
                <DroppableButton
                    key={type.code}
                    droppableId={typeFilterDropId(type.code)}
                    active={value === type.code}
                    droppable={droppable}
                    onClick={() => onChange(type.code)}
                    data-testid={`type-scope-${type.code}`}
                >
                    {type.label}
                    {getCount && <CountBadge count={getCount(type.code)}/>}
                </DroppableButton>
            ))}
        </div>
    )
}
